import React, { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { handleGetRequest } from '../../utils/api-handler.util'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import BackButtonAndTitle from '../../components/retailer-shop/BackButtonAndTitle'
import { useAuth } from '../../contexts/AuthContext'
import USER_TYPE from '../../constants/userType'
import AuthPromptModal from '../../components/common/AuthPromptModal'
import ProductImageCarousel from '../../components/common/ProductImageCarousel'
import SeoHelmet from '../../components/common/SeoHelmet'
import useProductCategories from '../../hooks/useProductCategories'
import DisplayShopName from '../../components/common/DisplayShopName'
import { ChatButton } from '../../components/common/ChatButton'
import { CallButton } from '../../components/common/CallButton'
import { CHAT_CONTEXT_TYPES } from '../../constants/contentTypes'
import DisplayForUserType from '../../components/auth/DisplayForUserType'

const WholesellerProductDetailsPage = () => {
  const { productSlug, shopSlug } = useParams()
  const { currentUser } = useAuth()
  const { getCategoryById } = useProductCategories()
  const userType = currentUser?.userType || USER_TYPE.guest

  const [showQuoteDetails, setShowQuoteDetails] = useState(false)

  const { data: productRequest, isLoading } = useQuery({
    queryKey: QueryKeys.shopProductDetails(productSlug, userType),
    queryFn: () => handleGetRequest(`/customers/${shopSlug}/products/${productSlug}`),
    enabled: !!productSlug,
  })

  const product = productRequest?.data
  const images = product?.productImages || []
  const category = getCategoryById(product?.categoryId)

  const handleRequestQuote = () => setShowQuoteDetails(true)

  if (isLoading) return <LoadingSpinner />
  if (!product) return <div>Product not found.</div>

  return (
    <main className="main-container">
      <SeoHelmet
        title={product?.name ? `${product.name} - Request Quote` : 'Request Quote'}
        titleSuffix="Wholesale from Eastleigh"
        description={
          product?.description.length > 150
            ? product?.description.slice(0, 147) + '...'
            : product?.description ||
              `Request wholesale quote for ${
                product?.name || 'this product'
              } from trusted Eastleigh wholesaler.`
        }
        image={product?.productImages?.[0]?.imageUrl || ''}
        url={`/wholesellers/${shopSlug}/products/${productSlug}/request-quote`}
      />
      <div className="mx-auto bg-background rounded-xl shadow-md overflow-hidden pb-8">
        <div className=" px-4 pt-6 pb-2">
          <BackButtonAndTitle
            title="View Product Details"
            navigateTo={`/wholesellers/${shopSlug}/products`}
            className="mb-4"
          />
          <ProductImageCarousel
            productImages={images}
            className="rounded-xl overflow-hidden w-64 h-64 mx-auto my-2"
          />
        </div>
        <div className="px-6">
          <div className="mb-3">
            <h1 className="text-lg font-bold text-black">{product.name}</h1>
            {category && (
              <Link to={`/categories/${category.categorySlug}`}>
                <div className="inline-block px-2 py-1 mt-1 text-xs font-medium text-gray-600 bg-neutral-200-100 rounded-full hover:bg-neutral-200 transition-colors cursor-pointer">
                  {category.name}
                </div>
              </Link>
            )}
            {product?.shopName && (
              <Link to={`/${product?.shopType}s/${product?.shopSlug}/products`}>
                <div className="text-[#0C74D6] text-base font-medium  mt-1">
                  <DisplayShopName
                    shop={{
                      name: product?.shopName,
                      isPremium: product.isShopPremium,
                      isVerified: product.isShopVerified,
                    }}
                  />
                </div>
              </Link>
            )}
          </div>
          <p className="text-sm text-gray-600 mb-6">{product.description}</p>
        </div>
        <div className="px-6">
          {!showQuoteDetails ? (
            <AuthPromptModal>
              <button
                onClick={handleRequestQuote}
                className="w-full px-6 py-3 text-lg font-semibold text-white bg-gradient-to-r from-[#D17707] to-[#E88F20] rounded-lg shadow hover:opacity-90 transition"
              >
                Request Quote
              </button>
            </AuthPromptModal>
          ) : (
            <div className="w-full bg-[#FFF4E5] rounded-lg px-6 py-4 mb-4">
              <p className="text-base text-gray-700 font-medium mb-2">
                <span className="text-black font-semibold">Price:</span> {product.priceText}
              </p>
              <p className="text-base text-gray-700 font-medium">
                <span className="text-black font-semibold">Minimum Order Quantity:</span>{' '}
                {product.minOrderQuantity}
              </p>
            </div>
          )}
        </div>
        <DisplayForUserType userTypes={[USER_TYPE.retailer]}>
          <div className="flex gap-4 px-6 mt-4">
            <ChatButton
              className="flex-1"
              chatConfig={{
                targetId: product?.shopId,
                contextType: CHAT_CONTEXT_TYPES.product,
                contextId: product?.shopProductId,
                targetUserType: USER_TYPE.wholeseller,
                description: (
                  <>
                    Contact the wholesaler for more details about <strong>{product?.name}</strong>
                  </>
                ),
              }}
            >
              Chat with Wholesaler
            </ChatButton>
            <CallButton
              className="flex-1"
              label="Call Wholesaler"
              targetPhoneNumber={product?.shopPhoneNumber}
            />
          </div>
        </DisplayForUserType>
      </div>
    </main>
  )
}

export default WholesellerProductDetailsPage
